/* ==========================================================================
   Main Product (revamp) — PDP buy box.
   Figma: AIPASurf.com, node 2046-1265 (desktop) / 2046-1384 (mobile).

   Components:
   - <product-gallery>          Swiper main + thumbs, syncs to variant image
   - <product-variant-picker>   computes the matched variant, updates the
                                 hidden `id` input used by the native ATC form,
                                 and broadcasts `product:variant-change`
   - <product-price>            re-renders on variant change
   - <product-buy-button>       enables/disables + relabels on variant change
   - <product-quantity>         +/- stepper
   - <product-viewing-count>    randomized "N people are looking at this"
   - <product-accordion-item>   single collapsible row

   Add-to-cart submission itself is untouched: the form still posts to
   /cart/add and is picked up by the site-wide capture-phase listener in
   assets/cart-drawer.js, so the cart drawer behavior stays identical to the
   rest of the theme.
   ========================================================================== */

(function () {
  'use strict';

  var VARIANT_CHANGE_EVENT = 'product:variant-change';

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
      this.mainEl = this.querySelector('[data-gallery-main]');
      this.thumbsRow = this.querySelector('[data-gallery-thumbs-row]');
      this.thumbsEl = this.querySelector('[data-gallery-thumbs]');
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.swiperWaitAttempts = 0;
      this.swiperWaitTimer = null;
      this.onVariantChange = this.onVariantChange.bind(this);
      this.waitForSwiper = this.waitForSwiper.bind(this);
    }

    connectedCallback() {
      this.waitForSwiper();
      document.addEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
      if (this.swiperWaitTimer) window.clearTimeout(this.swiperWaitTimer);
      this.destroySwiper();
    }

    /**
     * Swiper is loaded via a deferred <script> elsewhere in the theme
     * (assets/swiper-bundle.min.js, tagged in sections/cart-drawer.liquid).
     * Custom elements upgrade — and connectedCallback fires — the moment the
     * browser parses the tag, which happens well before any deferred script
     * runs, so `Swiper` is essentially never defined yet on first check.
     * Poll briefly until it's available instead of giving up silently.
     */
    waitForSwiper() {
      if (!this.isConnected) return;
      if (typeof Swiper !== 'undefined') {
        this.initSwiper();
        return;
      }
      this.swiperWaitAttempts += 1;
      if (this.swiperWaitAttempts > 200) return; // ~10s safety cutoff
      this.swiperWaitTimer = window.setTimeout(this.waitForSwiper, 50);
    }

    initSwiper() {
      if (!this.mainEl || this.mainSwiper) return;

      var slideCount = this.mainEl.querySelectorAll('.swiper-slide').length;
      var thumbsPerView = Number(this.dataset.thumbsPerView) || 3;
      var initialSlide = Number(this.dataset.initialIndex) || 0;

      if (slideCount <= 1) {
        if (this.thumbsRow) this.thumbsRow.hidden = true;
        if (this.prevBtn) this.prevBtn.hidden = true;
        if (this.nextBtn) this.nextBtn.hidden = true;
      } else if (this.thumbsEl) {
        this.thumbsSwiper = new Swiper(this.thumbsEl, {
          slidesPerView: Math.min(thumbsPerView, slideCount),
          spaceBetween: 16,
          watchSlidesProgress: true,
          initialSlide: initialSlide
        });
      }

      this.mainSwiper = new Swiper(this.mainEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide: initialSlide,
        thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
        on: {
          slideChange: this.updateNavState.bind(this),
          reachBeginning: this.updateNavState.bind(this),
          reachEnd: this.updateNavState.bind(this),
          fromEdge: this.updateNavState.bind(this)
        }
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          this.mainSwiper.slidePrev();
        }.bind(this));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          this.mainSwiper.slideNext();
        }.bind(this));
      }

      this.updateNavState();

      // Hand layout control over from the CSS fallback (flex/width rules
      // scoped to :not(.pdp-gallery--ready)) to Swiper's own inline styles.
      this.classList.add('pdp-gallery--ready');
    }

    updateNavState() {
      if (!this.mainSwiper) return;
      if (this.prevBtn) this.prevBtn.disabled = this.mainSwiper.isBeginning;
      if (this.nextBtn) this.nextBtn.disabled = this.mainSwiper.isEnd;
    }

    destroySwiper() {
      this.classList.remove('pdp-gallery--ready');
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }
    }

    onVariantChange(event) {
      var detail = event.detail || {};
      if (!detail.variant || !detail.variant.featured_media) return;
      if (String(detail.productId) !== String(this.productId)) return;
      this.goToMedia(detail.variant.featured_media.id);
    }

    goToMedia(mediaId) {
      if (!this.mainSwiper) return;
      var slides = this.mainEl.querySelectorAll('[data-media-id]');
      var index = Array.prototype.findIndex.call(slides, function (slide) {
        return slide.dataset.mediaId === String(mediaId);
      });
      if (index === -1) return;
      this.mainSwiper.slideTo(index);
    }
  }

  window.customElements.get('product-gallery') || window.customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------ */
  /* Variant picker                                                       */
  /* ------------------------------------------------------------------ */

  class ProductVariantPicker extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
      this.onInputChange = this.onInputChange.bind(this);
    }

    connectedCallback() {
      var json = this.querySelector('[data-variant-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
      this.updateAvailability();
      this.addEventListener('change', this.onInputChange);
    }

    getSelectedOptions() {
      var fieldsets = Array.prototype.slice.call(this.querySelectorAll('[data-option-position]'));
      fieldsets.sort(function (a, b) {
        return Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition);
      });
      return fieldsets.map(function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return value === options[index];
        });
      });
    }

    onInputChange(event) {
      if (!event.target.matches('[data-option-input]')) return;
      var options = this.getSelectedOptions();
      var variant = this.findVariant(options);
      this.updateLegends();
      this.updateAvailability();
      this.updateForm(variant);
      this.updateURL(variant);
      this.dispatchVariantChange(variant);
    }

    updateLegends() {
      this.querySelectorAll('[data-option-position]').forEach(function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        var group = fieldset.closest('.pdp-variant-picker__group');
        var valueEl = group ? group.querySelector('[data-option-value]') : null;
        if (checked && valueEl) valueEl.textContent = checked.value;
      });
    }

    updateAvailability() {
      var selected = this.getSelectedOptions();
      var variants = this.variants;
      this.querySelectorAll('[data-option-position]').forEach(function (fieldset) {
        var position = Number(fieldset.dataset.optionPosition);
        fieldset.querySelectorAll('[data-option-input]').forEach(function (input) {
          var candidate = selected.slice();
          candidate[position] = input.value;
          var match = variants.find(function (variant) {
            return variant.options.every(function (value, index) {
              return candidate[index] == null || value === candidate[index];
            });
          });
          // input and label are adjacent siblings (not parent/child), so the
          // native :checked + .option CSS selector can style the selected
          // state — .closest() would only search ancestors and miss it.
          var label = input.nextElementSibling;
          if (label) label.classList.toggle('pdp-variant-picker__option--soldout', !(match && match.available));
        });
      });
    }

    updateForm(variant) {
      if (!this.form) return;
      var idInput = this.form.querySelector('input[name="id"]');
      if (!idInput || !variant) return;
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateURL(variant) {
      if (!variant || !this.dataset.productUrl || this.hasAttribute('data-no-url-update')) return;
      window.history.replaceState({}, '', this.dataset.productUrl + '?variant=' + variant.id);
    }

    dispatchVariantChange(variant) {
      document.dispatchEvent(new CustomEvent(VARIANT_CHANGE_EVENT, {
        detail: { productId: this.productId, variant: variant }
      }));
    }
  }

  window.customElements.get('product-variant-picker') || window.customElements.define('product-variant-picker', ProductVariantPicker);

  /* ------------------------------------------------------------------ */
  /* Price                                                                */
  /* ------------------------------------------------------------------ */

  class ProductPrice extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.productId = this.dataset.productId;
      this.badgeTemplate = this.dataset.badgeTemplate || 'Save [amount]';
      this.currentEl = this.querySelector('[data-price-current]');
      this.compareEl = this.querySelector('[data-price-compare]');
      this.badgeEl = this.querySelector('[data-price-badge]');
      document.addEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    onVariantChange(event) {
      var detail = event.detail || {};
      if (!detail.variant || String(detail.productId) !== String(this.productId)) return;
      this.render(detail.variant);
    }

    render(variant) {
      if (typeof Shopify === 'undefined' || !Shopify.formatMoney) return;
      var format = window.money_format;
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (this.currentEl) this.currentEl.textContent = Shopify.formatMoney(variant.price, format);

      if (this.compareEl) {
        this.compareEl.hidden = !onSale;
        if (onSale) this.compareEl.textContent = Shopify.formatMoney(variant.compare_at_price, format);
      }

      if (this.badgeEl) {
        this.badgeEl.hidden = !onSale;
        if (onSale) {
          var amount = Shopify.formatMoney(variant.compare_at_price - variant.price, format);
          this.badgeEl.textContent = this.badgeTemplate.replace('[amount]', amount);
        }
      }

      this.hidden = false;
    }
  }

  window.customElements.get('product-price') || window.customElements.define('product-price', ProductPrice);

  /* ------------------------------------------------------------------ */
  /* Buy button                                                           */
  /* ------------------------------------------------------------------ */

  var BUY_BUTTON_TOOLTIP_DURATION = 5000;

  class ProductBuyButton extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onCartError = this.onCartError.bind(this);
      this.hideTooltip = this.hideTooltip.bind(this);
    }

    connectedCallback() {
      this.productId = this.dataset.productId;
      this.addToCartText = this.dataset.addToCartText || 'Add to cart';
      this.soldOutText = this.dataset.soldOutText || 'Sold out';
      this.unavailableText = this.dataset.unavailableText || 'Unavailable';
      this.button = this.querySelector('[data-buy-button-submit]');
      this.form = this.button ? this.button.closest('form') : null;
      this.tooltip = null;
      this.tooltipTimer = null;
      document.addEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
      document.addEventListener('cart-drawer:error', this.onCartError);
      document.addEventListener('cart-drawer:added', this.hideTooltip);
      if (this.button) this.button.addEventListener('click', this.hideTooltip);
    }

    disconnectedCallback() {
      document.removeEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
      document.removeEventListener('cart-drawer:error', this.onCartError);
      document.removeEventListener('cart-drawer:added', this.hideTooltip);
      if (this.tooltipTimer) window.clearTimeout(this.tooltipTimer);
    }

    onCartError(event) {
      var detail = event.detail || {};
      // Only react to failures from this exact form — a page can have more
      // than one add-to-cart form (e.g. a quick-add elsewhere) and this
      // button should only ever speak for its own submission.
      if (!this.form || detail.form !== this.form) return;

      var error = detail.error;
      var message =
        (error && (error.description || error.message)) ||
        (typeof error === 'string' ? error : null) ||
        'Something went wrong. Please try again.';
      this.showTooltip(message);
    }

    showTooltip(message) {
      if (!this.tooltip) {
        this.tooltip = document.createElement('div');
        this.tooltip.className = 'pdp-buy-button__tooltip';
        this.tooltip.setAttribute('role', 'alert');
        this.appendChild(this.tooltip);
      }
      this.tooltip.textContent = message;
      this.tooltip.classList.add('is-visible');
      if (this.tooltipTimer) window.clearTimeout(this.tooltipTimer);
      this.tooltipTimer = window.setTimeout(this.hideTooltip, BUY_BUTTON_TOOLTIP_DURATION);
    }

    hideTooltip() {
      if (this.tooltip) this.tooltip.classList.remove('is-visible');
    }

    onVariantChange(event) {
      var detail = event.detail || {};
      if (String(detail.productId) !== String(this.productId) || !this.button) return;

      if (!detail.variant) {
        this.button.setAttribute('disabled', 'disabled');
        this.button.textContent = this.unavailableText;
        return;
      }

      if (detail.variant.available) {
        this.button.removeAttribute('disabled');
        this.button.textContent = this.addToCartText;
      } else {
        this.button.setAttribute('disabled', 'disabled');
        this.button.textContent = this.soldOutText;
      }
    }
  }

  window.customElements.get('product-buy-button') || window.customElements.define('product-buy-button', ProductBuyButton);

  /* ------------------------------------------------------------------ */
  /* Quantity stepper                                                     */
  /* ------------------------------------------------------------------ */

  class ProductQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-quantity-input]');
      this.minusBtn = this.querySelector('[data-quantity-minus]');
      this.plusBtn = this.querySelector('[data-quantity-plus]');
      if (this.minusBtn) this.minusBtn.addEventListener('click', this.step.bind(this, -1));
      if (this.plusBtn) this.plusBtn.addEventListener('click', this.step.bind(this, 1));
      if (this.input) this.input.addEventListener('change', this.clamp.bind(this));
    }

    step(delta) {
      if (!this.input) return;
      var min = Number(this.input.min) || 1;
      var value = (parseInt(this.input.value, 10) || 0) + delta;
      if (value < min) value = min;
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clamp() {
      var min = Number(this.input.min) || 1;
      var value = parseInt(this.input.value, 10);
      if (isNaN(value) || value < min) value = min;
      this.input.value = value;
    }
  }

  window.customElements.get('product-quantity') || window.customElements.define('product-quantity', ProductQuantity);

  /* ------------------------------------------------------------------ */
  /* Viewing count                                                        */
  /* ------------------------------------------------------------------ */

  var VIEWING_COUNT_MIN_INTERVAL = 15000; // 15s
  var VIEWING_COUNT_MAX_INTERVAL = 240000; // 4 minutes

  class ProductViewingCount extends HTMLElement {
    constructor() {
      super();
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
    }

    connectedCallback() {
      this.min = Number(this.dataset.min) || 1;
      this.max = Number(this.dataset.max) || this.min;
      if (this.max < this.min) this.max = this.min;
      this.template = this.dataset.template || '[count] people are looking at this!';
      this.textEl = this.querySelector('[data-viewing-text]');
      this.render();
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      if (this.updateTimer) window.clearTimeout(this.updateTimer);
    }

    render() {
      var count = Math.floor(Math.random() * (this.max - this.min + 1)) + this.min;
      if (this.textEl) this.textEl.textContent = this.template.replace('[count]', count);
    }

    scheduleUpdate() {
      var delay = Math.floor(
        Math.random() * (VIEWING_COUNT_MAX_INTERVAL - VIEWING_COUNT_MIN_INTERVAL + 1)
      ) + VIEWING_COUNT_MIN_INTERVAL;
      this.updateTimer = window.setTimeout(function () {
        this.render();
        this.scheduleUpdate();
      }.bind(this), delay);
    }
  }

  window.customElements.get('product-viewing-count') || window.customElements.define('product-viewing-count', ProductViewingCount);

  /* ------------------------------------------------------------------ */
  /* Accordion item                                                       */
  /* ------------------------------------------------------------------ */

  class ProductAccordionItem extends HTMLElement {
    constructor() {
      super();
      this.header = this.querySelector('[data-accordion-header]');
      this.panel = this.querySelector('[data-accordion-panel]');
      this.content = this.querySelector('[data-accordion-content]');
      this.toggle = this.toggle.bind(this);
    }

    connectedCallback() {
      if (this.header) this.header.addEventListener('click', this.toggle);
      this.setOpen(this.hasAttribute('open'), false);
    }

    disconnectedCallback() {
      if (this.header) this.header.removeEventListener('click', this.toggle);
    }

    toggle() {
      this.setOpen(!this.hasAttribute('open'));
    }

    setOpen(open, animate) {
      if (!this.panel) return;
      animate = animate !== false;
      var panel = this.panel;

      if (open) {
        this.setAttribute('open', '');
        if (this.header) this.header.setAttribute('aria-expanded', 'true');
        var target = (this.content || panel).scrollHeight;
        panel.style.height = target + 'px';
        if (animate) {
          panel.addEventListener('transitionend', function onEnd() {
            panel.removeEventListener('transitionend', onEnd);
            panel.style.height = 'auto';
          });
        } else {
          panel.style.height = 'auto';
        }
      } else {
        var current = panel.scrollHeight;
        panel.style.height = current + 'px';
        // force reflow so the browser has a starting height to transition from
        void panel.offsetHeight;
        this.removeAttribute('open');
        if (this.header) this.header.setAttribute('aria-expanded', 'false');
        if (animate) {
          requestAnimationFrame(function () {
            panel.style.height = '0px';
          });
        } else {
          panel.style.height = '0px';
        }
      }
    }
  }

  window.customElements.get('product-accordion-item') || window.customElements.define('product-accordion-item', ProductAccordionItem);
})();
